import { LogManager } from '../services/log-manager'
import { LogType } from '../../common/schemas/enums'
import {
  GroupConfig,
  ResourceBidParameters,
  PageContentParameters,
  PageLookupParameters
} from '../../common/schemas/public'
import axios from 'axios'

type ResourceRequestData = {
  domain: string,
  groupId: string,
  videoId: string,
  placementTypeId: number,
  insertSizing: string,
  akuid: string,
  eids: any,
  iiqEIDs: any,
  GPPString: string,
  GPPSids: number[],
  TCString: string,
  GDPRApplies: number,
  USPString: string,
  sessionId: string,
  bidId: string,
  mediaId: string,
  logicalVideoId: string,
  startMilliseconds: number,
  endMilliseconds: number,
  idx?: number,
  outbrainToken?: string,
  marketplaceId: string,
  storeId: string,
  bundleId: string
}

const apiHost = process.env.API_HOST
const playerEventHost = process.env.PLAYEREVENT_HOST
let apilm: LogManager = null;

export function setApiLm(lm: LogManager) {
  apilm = lm;
}

function apiDebugLog(txt: string | any[], type: LogType | LogType[]=[LogType.QA]) {
  apilm.evaluateDebugLog(txt, type);
}

export async function postPlayerEvent(playerEvent)
{
  return await axios.post(`${playerEventHost}/api/v1/player-event`, playerEvent).catch((e) => {
    if(!e.response){
      apiDebugLog(`Error contacting server: ${playerEventHost}/api/v1/player-event`, LogType.ERROR);
    }else{
      apiDebugLog("Error sending PlayerEvent: (" + e + " )", LogType.ERROR);
    }
  });
}

export async function postDebugLogEvent(debugLogEvent)
{
  return await axios.post(`${playerEventHost}/api/v1/void`, debugLogEvent).catch((e) => {
    if(!e.response){
      apiDebugLog(`Error contacting server: ${playerEventHost}/api/v1/void`, LogType.ERROR);
    }else{
      apiDebugLog("Error sending DebugLogEvent: (" + e + " )", LogType.ERROR);
    }
  });
}

export async function adBlockerCheck()
{
  return axios.head('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js')
    .then(() => {
      // There is no AdBlocker
      return false;
    }).catch(() => {
      // Failed, Because of an AdBlocker
      return true;
    });
} 

export async function getTrackCues (
  groupId: string,
  videoUrl: string,
  videoDuration: number
): Promise<any> {
  let domain = 'null'

  if (window.location !== window.parent.location) {
    apiDebugLog('iframe detected', LogType.CXO)
    if(document.domain !== '' && document.domain !== undefined) {
      domain = document.domain.replace('www.', '')
    }
    if (document.referrer !== '') {
      apiDebugLog('loading domain from referrer', LogType.CXO)
      domain = new URL(document.referrer).hostname.replace('www.', '')
      apiDebugLog(`${domain} from referrer`, LogType.CXO)
    }

  } else {
    if(document.domain !== '' && document.domain !== undefined) {
      domain = document.domain.replace('www.', '')
    }
  }

  const url = `${apiHost}/api/v1/monetize/moments/${groupId}/${domain}/${videoDuration}?url=${videoUrl}`

  apiDebugLog(`initaiting trackcue Fetch ${url}`, LogType.CXO)
  return axios.get(url).then((response) => {
    return response.data;
  }).catch((e) => {
    apiDebugLog(['api error: ', e], LogType.ERROR)
    return null
  })
}

export async function fetchVASTWrapper (
  vastURL: string
): Promise<string> {

  apiDebugLog(`initaiting vast wrapper Fetch ${vastURL}`, LogType.CXO)
  return axios.get(vastURL).then((response) => {
    return response.data.text();
  }).catch((e) => {
    apiDebugLog(['api error: ', e], LogType.ERROR)
    return null
  })
}

export async function fetchVASTVideo (
  vastURL: string
): Promise<string> {

  apiDebugLog(`initaiting vast video Fetch ${vastURL}`, LogType.CXO)
  return axios.get(vastURL).then((response) => {
    return response.data.text();
  }).catch((e) => {
    apiDebugLog(['api error: ', e], LogType.ERROR)
    return null
  })
}

export async function fetchResourceData (
  bidParameters: ResourceBidParameters
): Promise<any> {
  let domain = 'null'

  if(document.location.host !== '' && document.location.host !== undefined) {
    domain = document.location.host.replace('www.', '')
  }
  if (window.location !== window.parent.location) {
    apiDebugLog('iframe detected', LogType.CXO)
    if (document.referrer !== '') {
      apiDebugLog('loading domain from referrer', LogType.CXO)
      domain = new URL(document.referrer).hostname.replace('www.', '')
      apiDebugLog(`${domain} from referrer`, LogType.CXO)
    }
  }

  const sizeRequest: string = (bidParameters.nativeSize !== null)?`${bidParameters.bannerSize}_${bidParameters.nativeSize}`:bidParameters.bannerSize;

  const url: string = (bidParameters.isRevContentBid)?`${apiHost}/api/v1/monetize/resources/revcontent`: (bidParameters.isOutbrainBid)?`${apiHost}/api/v1/monetize/resources/outbrain`: `${apiHost}/api/v1/monetize/resources`

  bidParameters.uid = (bidParameters.isOutbrainBid && bidParameters.uid === 'NOT_FOUND')?'null':bidParameters.uid;

  const data: ResourceRequestData = {
    groupId: bidParameters.groupId,
    domain: domain,
    videoId: bidParameters.videoId,
    placementTypeId: bidParameters.placementTypeId,
    insertSizing: sizeRequest,
    akuid: bidParameters.uid,
    eids: bidParameters.eidsIdentityHub,
    iiqEIDs: bidParameters.eidsIntentIQ,
    GPPString: bidParameters.gppString,
    GPPSids: bidParameters.gppSids,
    TCString: bidParameters.tcString,
    GDPRApplies: bidParameters.gdprApplies,
    USPString: bidParameters.uspString,
    sessionId: bidParameters.sessionId,
    bidId: bidParameters.bidId,
    mediaId: bidParameters.mediaId,
    logicalVideoId: bidParameters.logicalVideoId,
    startMilliseconds: (bidParameters.endMilliseconds < 2147483647)?bidParameters.startMilliseconds:5000,
    endMilliseconds: (bidParameters.endMilliseconds < 2147483647)?bidParameters.endMilliseconds:20000,
    marketplaceId: bidParameters.marketplaceId,
    storeId: bidParameters.storeId,
    bundleId: bidParameters.bundleId
  }
  if(typeof bidParameters.idx !== 'undefined' && bidParameters.idx !== null){
    data.idx = bidParameters.idx;
  }
  if(typeof bidParameters.outbrainToken !== 'undefined' && bidParameters.outbrainToken !== null){
    data.outbrainToken = bidParameters.outbrainToken;
  }

  apiDebugLog([`Fetching ${(bidParameters.isRevContentBid)?'Rev Content':(bidParameters.isOutbrainBid)?'Outbrain':'ADK'} resource:`, data], [LogType.QA]);
  return axios.post(url, data).then((response) => {
    return response.data;
  }).catch((e) => {
    apiDebugLog([`Error on ${(bidParameters.isRevContentBid)?'Rev Content':(bidParameters.isOutbrainBid)?'Outbrain':'ADK'} resource request: `, e], LogType.ERROR);
    return {}
  })

}

export async function initiatePageLookup(groupId: string, pageParameters: PageLookupParameters): Promise<any> {
  const url: string = `${apiHost}/api/v1/prebid/${groupId}/page/lookup`;
  const sendTime: number = (new Date()).getTime();
  apiDebugLog([`Sending page for lookup: ${pageParameters.pageUrl}`], [LogType.QA]);
  const b64post: string = btoa(JSON.stringify(pageParameters));
  //apiDebugLog([`decoded bid enrich page url: `, JSON.parse(atob(b64post))], LogType.QA);
  return axios.post(url, b64post, {headers: {"Content-Type": "text/plain"}}).then((response) => {
    const responseTime: number = (new Date()).getTime();
    apiDebugLog([`initiatePageLookup response status (${response.status}) received in ${responseTime-sendTime}ms.`, response.data?.content], LogType.QA);
    return response.data?.content;
  }).catch((e) => {
    apiDebugLog([`Error on initiatePageLookup post with status ${e.response?.status}.`], [LogType.QA, LogType.ERROR]);
    return null;
  })
}

export async function initiatePageAnalysis(groupId: string, pageParameters: PageContentParameters): Promise<number> {
  const url: string = `${apiHost}/api/v1/prebid/${groupId}/page/index`;
  const sendTime: number = (new Date()).getTime();
  apiDebugLog([`Sending page data for analysis:`, pageParameters], [LogType.QA]);
  const b64post: string = btoa(JSON.stringify(pageParameters));
  return axios.post(url, b64post, {headers: {"Content-Type": "text/plain"}}).then((response) => {
    const responseTime: number = (new Date()).getTime();
    apiDebugLog(`initiatePageAnalysis response status (${response.status}) received in ${responseTime-sendTime}ms.`, LogType.QA);
    return response.status;
  }).catch((e) => {
    apiDebugLog([`Error on initiatePageAnalysis post with status ${e.response?.status}.`], [LogType.QA, LogType.ERROR]);
    return e.response?.status;
  })
}

export async function fetchGroupConfigs (
  groupId: string
): Promise<GroupConfig> {
  let domain = window.location.hostname || 'null';
  if (window.self !== window.top) {
    apiDebugLog('iframe detected', LogType.CXO)
    try{
      domain = window.top.location.hostname;
    }catch(err){
      let referrerUrl: string = window.location?.ancestorOrigins?.[window.location.ancestorOrigins.length-1] || window.document.referrer || null;
      if(referrerUrl !== null){
        domain = new URL(referrerUrl).hostname;
      }
      apiDebugLog(`iframe is not friendly to access window.top - referrer obtained ${domain}`, LogType.CXO);
    }
  }
  domain = domain.replace('www.','') || 'null';

  return axios.get(`${apiHost}/api/v1/group/configs/${groupId}/${domain}`).then((response) => {
    return response.data;
  }).catch((e) =>{
    apiDebugLog(['api error: ', e], LogType.ERROR)
    return null
  })
}
